import React, { Fragment, useEffect } from 'react';
import { capitalize } from 'core/utils/text';
import { InfoBadge, Text, Label, Heading } from '@medely/ui-kit';
import { Box, Card, Divider } from '@medely/web-components';
import { ChevronRight } from '@medely/web-components/icons';
import ConditionFormFields from 'components/credentials/ConditionFormFields';
import {
  useMissingConditionForm,
  MissingConditionFormQualification,
} from 'components/credentials/conditionDialogs/MissingConditionForm';
import { MissingConditionFormOption } from './MissingConditionFormDialog';
import type { IQualification, ICondition } from '@medely/web-components/types';
import { useTrackQualification } from 'hooks/useTrackQualification';

interface IMissingConditionFormOptionProps {
  label?: string;
  option: MissingConditionFormOption;
  onClick: (qualification: MissingConditionFormQualification) => void;
}

interface IMissingSingleConditionQualificationStatus {
  option: MissingConditionFormOption;
  qualification: MissingConditionFormQualification;
}

const MissingSingleConditionQualificationStatus = ({
  option,
  qualification,
}: IMissingSingleConditionQualificationStatus) => {
  const status = option.qualificationToProfessionalQualification[qualification.id]?.status;

  if (status === 'approved') {
    return <InfoBadge color="success" label="Active" />;
  }
  if (status === 'expires_soon') {
    const expirationDate =
      option.qualificationToProfessionalQualification[qualification.id]?.expiration_date;
    return <InfoBadge color="warning" label={`Expires ${expirationDate}`} />;
  }
  if (status === 'rejected') {
    return <InfoBadge color="error" label="Rejected" />;
  }
  if (status === 'expired') {
    return <InfoBadge color="error" label="Expired" />;
  }
  if (option.willBeSatisfied || status === 'review') {
    return <InfoBadge color="info" label="In Review" />;
  }
  return null;
};

const QualificationsForOption = ({ label, option, onClick }: IMissingConditionFormOptionProps) => {
  const singleMissing = option.qualifications.length === 1;
  const title = option.qualifications[0]?.long_name || option.description;

  const handleClick = (qualification?: IQualification) => {
    onClick(qualification || option.qualifications[0]);
  };

  return (
    <Card>
      <Box p={2} pb={1}>
        {label && (
          <Box mb={1}>
            <Label size="xs" color="state.teal.secondary">
              {label}
            </Label>
          </Box>
        )}
        <Box>
          {option.qualifications.map((qualification, index) => (
            <Fragment key={String(qualification.id)}>
              {index > 0 && <Divider />}
              <Box
                pb={1.5}
                pt={index > 0 ? 1.5 : 1}
                display="flex"
                alignItems="center"
                gap={2}
                sx={{ cursor: 'pointer' }}
                onClick={() => handleClick(qualification)}
              >
                <Box
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap={0.5}
                >
                  <Label size="m">
                    {capitalize(singleMissing ? title : qualification.long_name)}
                  </Label>
                  {qualification.description && (
                    <Text size="m" color="text.secondary">
                      {qualification.description}
                    </Text>
                  )}
                </Box>
                <MissingSingleConditionQualificationStatus
                  option={option}
                  qualification={qualification}
                />
                <ChevronRight color="disabled" />
              </Box>
            </Fragment>
          ))}
        </Box>
      </Box>
    </Card>
  );
};

const UploadMissingCondition = ({
  selectedOption,
  selectedQualificationIndex,
}: {
  selectedOption: MissingConditionFormOption;
  selectedQualificationIndex: number;
}) => {
  const { trackQualificationStartEditing, trackQualificationFinishEditing } =
    useTrackQualification();
  const qualification = selectedOption.qualifications[selectedQualificationIndex];

  useEffect(() => {
    if (qualification) {
      trackQualificationStartEditing({ qualificationId: qualification.id });
    }

    return () => {
      if (qualification) {
        trackQualificationFinishEditing({ qualificationId: qualification.id });
      }
    };
  }, [qualification.id]);

  return (
    <ConditionFormFields
      name={`condition.qualifications[${selectedQualificationIndex}]`}
      condition={{ qualifications: [qualification] }}
      withQualificationName
    />
  );
};

interface IMissingConditionFormOptionsPickerProps {
  condition: ICondition;
  options: MissingConditionFormOption[];
  selectOption: (
    option: MissingConditionFormOption,
    qualification: MissingConditionFormQualification,
  ) => void;
}

const MissingConditionFormOptionsPicker = ({
  condition,
  options,
  selectOption,
}: IMissingConditionFormOptionsPickerProps) => {
  return (
    <Box data-testid="missing-condition-options-picker">
      <Heading size="m">{condition.label}</Heading>
      <Box mt={0.5}>
        <Text size="m">Complete one of the following options.</Text>
      </Box>
      {options.map((option, index) => (
        <Box key={index} mt={3}>
          <QualificationsForOption
            label={`Option ${index + 1}`}
            option={option}
            onClick={(qualification) => selectOption(option, qualification)}
          />
        </Box>
      ))}
    </Box>
  );
};

const MissingConditionFormOptions = () => {
  const { condition, selectedOption, selectOption, options, selectedQualificationIndex } =
    useMissingConditionForm();

  if (!options.length || !condition) {
    return null;
  }

  return (
    <>
      {!selectedOption ? (
        <MissingConditionFormOptionsPicker
          condition={condition}
          options={options}
          selectOption={selectOption}
        />
      ) : (
        <UploadMissingCondition
          selectedOption={selectedOption}
          selectedQualificationIndex={selectedQualificationIndex}
        />
      )}
    </>
  );
};

export default MissingConditionFormOptions;
